/**
 * The sounds browser.
 *
 * Exists for a concrete reason, not for completeness: the Strudel concept graph's
 * `sound_function` node lists "Finds a new sound in the sounds browser and uses it"
 * as an evidence signal, and until now there was no browser to find one in — the
 * tutor would send a learner looking for a strudel.cc feature this app did not have.
 *
 * It also answers the question a learner cannot otherwise answer: a name that is not
 * in the pack plays *silence* rather than erroring, so "is `oh` a real sound?" had no
 * way of being resolved except by asking an adult.
 */

import { useMemo, useState } from "react";

import { filterSounds } from "../sounds";
import type { SoundsCatalogue } from "../sounds";

interface Props {
  catalogue: SoundsCatalogue | null;
  loading: boolean;
  error: string | null;
  onPick: (name: string) => void;
}

export function SoundsBrowser({ catalogue, loading, error, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => filterSounds(catalogue, query), [catalogue, query]);
  const count = catalogue?.banks.length ?? 0;

  return (
    <section className="sounds">
      <button
        type="button"
        className="sounds-toggle"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
      >
        {open ? "▾" : "▸"} Sounds{count ? ` (${count})` : ""}
      </button>

      {open && (
        <div className="sounds-body">
          {loading && <div className="muted">Loading the sound list…</div>}
          {error && <div className="sounds-error">{error}</div>}

          {catalogue && (
            <>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search — try 'hat', 'snare', '808'"
                spellCheck={false}
                aria-label="Search sounds"
              />

              {filtered.missing.length > 0 && (
                <p className="sounds-missing">
                  {query.trim() ? "Not available here" : "Documented by Strudel but not in this pack"}
                  {": "}
                  {filtered.missing.map((name) => (
                    <code key={name}>{name}</code>
                  ))}
                  {" — these play "}
                  <strong>silence</strong>
                  {", not an error."}
                </p>
              )}

              {filtered.total === 0 && query.trim() && filtered.missing.length === 0 && (
                <p className="muted">
                  Nothing matches “{query.trim()}”. That name would play silence.
                </p>
              )}

              {filtered.kit.length > 0 && (
                <>
                  <h4>Drum kit</h4>
                  <ul className="sound-list">
                    {filtered.kit.map((bank) => (
                      <li key={bank.name}>
                        <button type="button" onClick={() => onPick(bank.name)}>
                          <code>{bank.name}</code>
                          <span className="sound-desc">{bank.description}</span>
                          <span className="sound-variants">{bank.variants}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {filtered.rest.length > 0 && (
                <>
                  <h4>Everything else</h4>
                  <ul className="sound-list compact">
                    {filtered.rest.map((bank) => (
                      <li key={bank.name}>
                        <button
                          type="button"
                          onClick={() => onPick(bank.name)}
                          title={`${bank.variants} variant${bank.variants === 1 ? "" : "s"} — pick one with ${bank.name}:n`}
                        >
                          <code>{bank.name}</code>
                          <span className="sound-variants">{bank.variants}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="muted sounds-foot">
                Click a name to drop it into your pattern. The number is how many
                variants there are — pick one with <code>name:2</code>.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
